import React from 'react';
import styled from '@emotion/styled';

import { PaddedModalBody } from './ModalContainers/ModalContainers';
import MediaDisplay from '../MediaDisplay';
import HandOffLink from '../../../utils/urls/HandOffLink';

import withTrackingPropsProvider from '../../../hocs/withTrackingPropsProvider';

import { DESKTOP_START_QUERY } from '../../../styling/mixins/responsive';

import CheckmarkCircle16 from '../../../assets/svg/CheckmarkCircle16';
import WindowHeader from '../../../assets/svg/WindowHeader';
import CardPoster from '../../../assets/ProductComparison/Card_Poster.jpg';
import CardVideo from '../../../assets/ProductComparison/Card.mp4';
import FlyerPoster from '../../../assets/ProductComparison/Flyer_Poster.jpg';
import FlyerVideo from '../../../assets/ProductComparison/Flyer.mp4';

const TopPaddedPaddedModalBody = withTrackingPropsProvider(styled(
  PaddedModalBody,
)`
  padding: 2.5rem 0 0;

  @media (${DESKTOP_START_QUERY}) {
    padding: 3rem 2.75rem 2.5rem;
  }
`);

const ComparisonVisual = styled.div`
  display: flex;
`;

const Product = styled.div`
  text-align: center;
  padding: 3rem;
  flex: 50%;
`;

const ProductVideo = styled.div`
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.08);

  video {
    max-width: 100%;
  }
`;

const ProductTitle = styled.h2`
  font-weight: 500;
  font-size: 1.5rem;
  margin: 1.5rem 0 0.5rem;
`;

const ProductDescription = styled.p`
  font-size: 1rem;
  line-height: 1.5rem;
  color: #777;
  max-width: 15rem;
  margin: auto;
`;

const Title = styled.div`
  width: 16rem;
  margin: 0 auto 4rem;
  font-size: 1.25rem;
  text-align: center;
  font-weight: 500;
  line-height: 1.2;

  @media (${DESKTOP_START_QUERY}) {
    width: 100%;
    color: gray;
    margin: 0;
    font-weight: 600;
    letter-spacing: 2px;
    font-size: 0.625rem;
    text-transform: uppercase;
  }
`;

const ComparisonTable = styled.table`
  border-collapse: collapse;
  width: 100%;
  text-align: center;
  vertical-align: center;
  font-size: 0.875rem;

  td,
  th {
    padding: 0.75rem;
    min-width: 3.5rem;
    text-align: center;
  }

  td:first-child,
  th:first-child {
    text-align: left;
  }

  tr:nth-child(odd) td {
    background-color: #f7f7f7;
  }

  thead th {
    border-bottom: 1px solid #eee;
    padding-bottom: 0.75rem;
  }

  @media (${DESKTOP_START_QUERY}) {
    font-size: 1rem;

    td,
    th {
      padding: 1rem;
      min-width: 6.5rem;
    }

    thead th {
      padding-bottom: 1rem;
    }
  }
`;

const SupportLink = styled.p`
  font-size: 1rem;
  color: #777;
  text-align: center;
  margin: 2.5rem 1.5rem 2rem;
  line-height: 1.5;

  a {
    color: #777;
    border-bottom: 1px solid #777;
    display: inline-block;
    padding-bottom: 1px;
    &:hover {
      color: #999;
      cursor: pointer;
      border-color: #bbb;
    }
  }
`;

export default function ComparisonModal(properties) {
  const { className, isMobile } = properties;

  return (
    <TopPaddedPaddedModalBody
      className={`pp-flyer-comp-modal ${className ? className : ''}`}
      tracking={{
        block_name: 'comparison_modal',
        block_row: 'comparison_modal',
      }}
      isMobile={isMobile}
    >
      <Title>What’s the difference between Flyers and Cards?</Title>
      {isMobile ? null : (
        <ComparisonVisual>
          <Product>
            <WindowHeader />
            <ProductVideo>
              <MediaDisplay
                media={{
                  type: 'video',
                  video: FlyerVideo,
                  poster: FlyerPoster,
                  image: FlyerPoster,
                  mobileImage: FlyerPoster,
                }}
                layout="center"
                imageSize="ModalContainers"
              />
            </ProductVideo>
            <ProductTitle>Flyers</ProductTitle>
            <ProductDescription>
              Modern templates you can customize in seconds
            </ProductDescription>
          </Product>
          <Product>
            <WindowHeader />
            <ProductVideo>
              <MediaDisplay
                media={{
                  type: 'video',
                  video: CardVideo,
                  mobileVideo: CardVideo,
                  poster: CardPoster,
                  image: CardPoster,
                  mobileImage: CardPoster,
                }}
                layout="center"
                imageSize="ModalContainers"
              />
            </ProductVideo>
            <ProductTitle>Cards</ProductTitle>
            <ProductDescription>
              Artisanal stationery designs with exclusive partner collections
            </ProductDescription>
          </Product>
        </ComparisonVisual>
      )}

      <ComparisonTable>
        <thead>
          <tr>
            <th>Feature comparison</th>
            <th>Flyers</th>
            <th>Cards</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              Email through Paperless Post to collect RSVPs, and track opens and
              bounces
            </td>
            <td>
              <CheckmarkCircle16 />
            </td>
            <td>
              <CheckmarkCircle16 />
            </td>
          </tr>
          <tr>
            <td>
              Share or text your invite with a free link. Follow up with guests
              directly once they RSVP
            </td>
            <td>
              <CheckmarkCircle16 />
            </td>
            <td />
          </tr>
          <tr>
            <td>Import CSV contacts, and export your guest list</td>
            <td />
            <td>
              <CheckmarkCircle16 />
            </td>
          </tr>
          <tr>
            <td>Follow up with all guests or groups of guests</td>
            <td>
              <CheckmarkCircle16 />
            </td>
            <td>
              <CheckmarkCircle16 />
            </td>
          </tr>
          <tr>
            <td>Reply to individual guests at any time</td>
            <td />
            <td>
              <CheckmarkCircle16 />
            </td>
          </tr>
          <tr>
            <td>Invite a co-host and set maximum capacity in advance</td>
            <td />
            <td>
              <CheckmarkCircle16 />
            </td>
          </tr>
        </tbody>
      </ComparisonTable>
      <SupportLink>
        Need help deciding?{' '}
        <HandOffLink
          href="https://www.paperlesspost.com/contact"
          identifier="Contact support"
        >
          Contact support.
        </HandOffLink>
      </SupportLink>
    </TopPaddedPaddedModalBody>
  );
}
